/*
 * Runtime configuration for the administration console.
 *
 * Its own variables, not the recruiter app's, even though three of the four currently hold the
 * same values. Sharing them would mean that pointing the console at a different API — which is
 * the normal state in any environment where the two are not co-deployed — required changing a
 * variable the recruiter app also reads.
 *
 * The client id genuinely differs: separate OIDC clients mean separate redirect URIs, so a code
 * minted for the console cannot be redeemed by the recruiter app.
 */

interface Config {
  readonly apiUrl: string;
  readonly oidcIssuer: string;
  readonly oidcClientId: string;
  readonly oidcRedirectUri: string;
  /**
   * Extra query parameters the primary provider requires on the authorization request.
   *
   * ── Why the primary needs what only the alt used to have — 08 Sep 2026 ────────────────────────
   *
   * `AltProvider.authorizeParams` below carries the argument: WorkOS refuses a bare authorization
   * request with `invalid-connection-selector`, and `provider=authkit` is the selector. That was an
   * alt-only concern for exactly as long as WorkOS was only ever the second door — which stopped
   * being true when staging dropped the fake IdP and WorkOS became its only provider.
   *
   * A build with no second provider and an unusable first one is the worst arrangement available,
   * and it would have failed as a redirect to error.workos.com with nothing in our logs. So the
   * field is here for the same reason it is there, and it stays optional: a provider that needs no
   * selector is configured with none.
   */
  readonly oidcAuthorizeParams?: Readonly<Record<string, string>>;

  /**
   * A same-origin path the token exchange is routed through, or absent to call the provider
   * directly.
   *
   * ── Why this is no longer only a diagnostic — 08 Sep 2026 ───────────────────────────────
   *
   * The recruiter app's copy of this field says a proxy in the authentication path is a
   * substitution, and that one always on stops being a diagnostic and becomes the thing under
   * test. That reasoning held while the proxy was a dev server. **It is reversed here for
   * staging deliberately**, and the first staging sign-in is what reversed it.
   *
   * WorkOS returned `200 OK` for the exchange -- the sign-in genuinely succeeded, tokens and
   * all -- and omitted `access-control-allow-origin`, although this origin is on the allow list
   * of the very application whose `client_id` the request carried. The browser therefore threw
   * the response away. Verified from outside: their preflight echoes *any* origin, including
   * one invented for the test, and their error replies carry no CORS headers at all, so
   * nothing observable distinguishes an allow-listed origin from a blocked one.
   *
   * On Netlify the forwarder is a rewrite in `public/_redirects` -- the hosting platform, not a
   * developer's machine -- so the deployed artifact is exactly what is under test, which is
   * the property the original warning was protecting. The provider still sees an identical
   * request; only who may read the answer changes.
   *
   * A prefix, not a route: the provider key is appended (`/idp/primary`), and one rewrite is
   * mounted per configured issuer.
   */
  readonly oidcErrorProxy?: string;
  readonly altOidc?: AltProvider;
}

/**
 * A second identity provider, when one is configured.
 *
 * ── Why an app can have two — 19 Aug 2026 ───────────────────────────────────────────────────────
 *
 * On a laptop the fake IdP authenticates every seeded principal — the browser suite and both seed
 * scripts depend on it — while WorkOS authenticates a real person against a manually provisioned
 * agency. Both are the same realm, both are live, and the API trusts both key sets (see
 * `RealmIdentities` in `identity`). The browser cannot infer which one a visitor wants, so it asks:
 * one button per configured provider.
 *
 * The same shape is what a provider migration needs in a deployed environment, which is why this
 * is a general capability rather than a development flag. Absent means one button and no change.
 *
 * **`issuer` here is a discovery base, not an `iss`.** For WorkOS the two differ: the browser
 * fetches `<application>/.well-known/openid-configuration` while every token is stamped with the
 * *environment's* issuer. Nothing in a browser verifies a token — the API does that — so this
 * value is only ever "where the endpoints are described".
 */
export interface AltProvider {
  readonly issuer: string;
  readonly clientId: string;
  /**
   * Extra query parameters the provider requires on the authorization request.
   *
   * ── Why this is configuration and not a branch in the client ─────────────────────────────────
   *
   * WorkOS refuses a bare authorization request with `invalid-connection-selector`: the standard
   * parameters do not say *which* flow is meant — hosted login, a named SSO connection, a specific
   * organisation — and it will not guess. `provider=authkit` selects the hosted login, and it is a
   * WorkOS word that appears in no other provider's vocabulary.
   *
   * The OIDC client is deliberately provider-agnostic — it learns its endpoints from a discovery
   * document precisely so that swapping providers is configuration. Writing `provider=authkit`
   * into it would undo that for the sake of one string, and the next provider would need its own
   * branch beside it. So the client appends whatever pairs it is given and never knows what they
   * mean; the runbook explains what WorkOS needs.
   *
   * Written as a query fragment — `a=b&c=d` — because that is the shape of the thing it becomes.
   */
  readonly authorizeParams?: Readonly<Record<string, string>>;
}

/**
 * `a=b&c=d` from the environment into pairs, or nothing.
 *
 * `URLSearchParams` rather than a hand-rolled split: it decodes percent-escapes, which a value
 * carrying a URL or a space will need, and it is the same parser that will re-encode them.
 */
function authorizeParams(raw: string | undefined): Readonly<Record<string, string>> | undefined {
  if (!raw) {
    return undefined;
  }
  const parsed = Object.fromEntries(new URLSearchParams(raw));
  return Object.keys(parsed).length > 0 ? parsed : undefined;
}

function optionalProvider(
  issuer: string | undefined,
  clientId: string | undefined,
  params: string | undefined,
): AltProvider | undefined {
  // Both or neither. Half a provider is a button that fails after the redirect, which is the worst
  // place for a configuration mistake to surface.
  if (!issuer || !clientId) {
    return undefined;
  }
  return { issuer, clientId, authorizeParams: authorizeParams(params) };
}

function required(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `${name} is not set. The admin console has no default for it — a build that silently ` +
        'pointed at the wrong API would be worse than one that refuses to start.',
    );
  }
  return value;
}

export const config: Config = {
  apiUrl: required('VITE_ADMIN_API_URL', import.meta.env.VITE_ADMIN_API_URL),
  oidcIssuer: required('VITE_ADMIN_OIDC_ISSUER', import.meta.env.VITE_ADMIN_OIDC_ISSUER),
  oidcClientId: required('VITE_ADMIN_OIDC_CLIENT_ID', import.meta.env.VITE_ADMIN_OIDC_CLIENT_ID),
  oidcRedirectUri: `${window.location.origin}/auth/callback`,
  oidcAuthorizeParams: authorizeParams(
    import.meta.env.VITE_ADMIN_OIDC_AUTHORIZE_PARAMS as string | undefined,
  ),
  // See the field: a diagnostic locally, and the only readable transport on staging.
  oidcErrorProxy: (import.meta.env.VITE_ADMIN_OIDC_ERROR_PROXY as string | undefined) || undefined,
  altOidc: optionalProvider(
    import.meta.env.VITE_ADMIN_OIDC_ALT_ISSUER as string | undefined,
    import.meta.env.VITE_ADMIN_OIDC_ALT_CLIENT_ID as string | undefined,
    import.meta.env.VITE_ADMIN_OIDC_ALT_AUTHORIZE_PARAMS as string | undefined,
  ),
};
